import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../contexts/app/AppContext";
import { useCategoryPageContext } from "../../contexts/app/CategoryPageContext";
import FlexBox from "../FlexBox";
import { Paragraph, SemiSpan, Span } from "../Typography";
import Accordion from "../accordion/Accordion";
import AccordionHeader from "../accordion/AccordionHeader";

import CloseSharpIcon from '@mui/icons-material/CloseSharp';
import useWindowSize from "../../hooks/useWindowSize";
import Box from "../Box";
import { AccordionWrapper } from "../accordion/AccordionStyle";
import IconButton from "../buttons/IconButton";
import Icon from "../icon/Icon";
import { ProductFilterCardStyled } from "./ProductFilterCardStyle";
import RangeSlider from "./RangeSlider";
import RangeSliderSearch from "./RangeSliderSearch";

interface Filter {
  id: number;
  filterCode: string;
}

interface FilterValue {
  id: number;
}

interface Filters {
  [key: string]: FilterValue[];
}

interface Context {
  filters: Filters;
  addFilter: (filterCode: string, id: number) => void;
}

interface Props {
  activeFiltersFromStorage: Filter[];
  context: Context;
}

function findUnmatchedFilters(activeFiltersFromStorage: Filter[], contextFilters: Filters): Filter[] {
  const unmatchedFilters: Filter[] = []

  activeFiltersFromStorage.forEach((filter) => {
    const { id, filterCode } = filter

    const matchingFilterArray = contextFilters[filterCode]?.filter(
      (filterValue: FilterValue) => filterValue.id === id
    );

    if (!matchingFilterArray?.length) {
      unmatchedFilters.push(filter)
    }
  });

  return unmatchedFilters;
}

const ProductFilterCard = (filters: any): Props => {

  const context = useCategoryPageContext()
  const { state } = useAppContext()
  const { addFilter, isSearch, categoryProducts, setFilterApply } = useCategoryPageContext()
  const [selectedOption, setSelectedOption] = useState(null)
  const [sortOption, setSortOption] = useState('')
  const screenWidth = useWindowSize()!
  const [activeColor, setActiveColor] = useState("white")
  const [activeFilterIDs, setActiveFilterIDs] = useState<number[]>([])
  const [activeFilters, setActiveFilters] = useState([])
  const { t } = useTranslation()

  const handleSort = (e: any) => {
    setFilterApply(true)
    addFilter('sort', e.value)
    setSortOption(e.label)
  }

  const sortOptions = [
    { label: t("filters.new"), value: "created_at,desc" },
    /*{ label: t("filters.old"), value: "created_at,asc" },*/
    { label: t("filters.cheap"), value: "ca_price,asc" },
    { label: t("filters.expensive"), value: "ca_price,desc" },
  ];

  function renderFilter(filter: any) {
    if (filter.type == 'price') {
      return renderRangeElement()
    } else if (filter.type == 'select') {
      return filter.options.length > 0 ? renderSelectElement(filter) : null
    }
    return null
  }

  const handleFilter = async (optionLabel: string, filterCode: string, optionID: number) => {

    setFilterApply(true)
    addFilter(filterCode, optionID);

    const newFilter = { id: optionID, name: optionLabel, filterCode: filterCode };
    const existingFilterIndex = activeFilters.findIndex(filter => filter.id === optionID);

    if (existingFilterIndex !== -1) {
      const newFilters = [...activeFilters];
      newFilters.splice(existingFilterIndex, 1);
      setActiveFilters(newFilters);
      setActiveFilterIDs(activeFilterIDs.filter(id => id !== optionID));

      // Check if the same option is clicked again, and reset the selection and active color if it is
      if (selectedOption === optionLabel) {
        setSelectedOption(null);
        setActiveColor("white");
      }
    } else {
      setActiveFilters([...activeFilters, newFilter]);
      setActiveFilterIDs([...activeFilterIDs, optionID]);

      // Only update the selection and active color if it's a new option
      if (selectedOption !== optionLabel) {
        setSelectedOption(optionLabel);
        setActiveColor("royalblue");
      }
    }

    if (filterCode === 'color') {
      setSelectedOption(null);
    }
  }

  const getLabel = (id: number) => {

    const optionsColor = filters.filters.find((filter: { code: string }) => filter.code === 'color')?.options
    const optionsSize = filters.filters.find((filter: { code: string }) => filter.code === 'size')?.options
    const label = optionsColor?.find((item: { id: number }) => item.id === id)

    //console.log('optionsColor => ', label)

    if (!label) {
      const label = optionsSize?.find((item: { id: number }) => item.id === id)
      return label.admin_name
    }
    return label.admin_name
  }

  const getSwatchValue = (id: number) => {

    const optionsColor = filters.filters.find((filter: { code: string }) => filter.code === 'color')?.options

    const swatchValue = optionsColor?.find((item: { id: number }) => item.id === id)
    return swatchValue?.swatch_value;

  }

  const getSwatchImageValue = (id: number) => {

    const optionsColor = filters.filters.find((filter: { code: string }) => filter.code === 'color')?.options

    const swatchImageValue = optionsColor?.find((item: { id: number }) => item.id === id)
    return swatchImageValue?.swatch_color_image_value;
  }

  const getLabelBrand = (id: number) => {
    const optionsBrand = filters.filters.find((filter: { code: string }) => filter.code === 'brand')?.options
    const label = optionsBrand?.find(item => item.id === id)

    if (label) {
      return label.admin_name
    }
  }

  const removeFilter = (index: number, filter: Array<Object>) => {

    const isColorFilter = filter.filterCode === 'color'

    // Remove the filter from the activeFilters array
    const newFilters = [...activeFilters]
    newFilters.splice(index, 1)
    setActiveFilters(newFilters)

    // If the filter is a color filter, update the activeColor state
    if (isColorFilter) {
      setActiveColor('white')
    }

    // If there are still active filters, update the activeFilterIDs state
    if (newFilters.length > 0) {
      const newActiveFilterIDs = newFilters.map((filter) => filter.id)
      setActiveFilterIDs(newActiveFilterIDs)
    } else {
      // If there are no active filters left, reset the activeFilterIDs state
      setActiveFilterIDs([])
    }

    // Add the filter back to the availableFilters array
    setFilterApply(true)
    addFilter(filter.filterCode, filter.id)
  }

  function renderRangeElement() {
    return (
      <>

        {/*--------------------------------------------- ΕΝΕΡΓΟ ΦΙΛΤΡΟ ----------------------------------------*/}

        {/* {activeFilters?.length > 0 &&
          <FlexBox width={'100%'} alignItems='flex-start' justifyContent={'space-between'} color='white' flexDirection={'column'}
            flexWrap='wrap' className='filter-item' >

            <Paragraph fontWeight='500' color="text.muted" ml="0.5rem">
              Ενεργά Φίλτρα
            </Paragraph>
            <FlexBox>
              {activeFilters.map((e, ind) => {
                return <Box cursor="pointer" ml={2} key={ind} onClick={() => removeFilter(ind, e)}> {e.name} </Box>
              })}
            </FlexBox>
          </FlexBox>

        } */}

        {/*--------------------------------------------ΕΝΕΡΓΑ ΦΙΛΤΡΑ ------------------------------------------- */}

        {context.filters &&

          <FlexBox width={'100%'} alignItems='flex-start' justifyContent={'space-between'} color='white' flexDirection={'column'}
            flexWrap='wrap' className='filter-item' >

            <Paragraph color="text.muted" ml="0.5rem">
              {t("filters.activefilters")}
            </Paragraph>
            <FlexBox flexWrap={'wrap'}>
              {context.filters.color?.map((e: number, ind: React.Key | null | undefined) => {
                return <FlexBox
                  borderRadius={10}
                  border={'solid 1px white'}
                  ml={2}
                  key={ind}
                  p={1}
                  alignItems={'center'}
                  minWidth={'50px'}
                  justifyContent={'space-around'}
                >
                  <CloseSharpIcon
                    cursor="pointer"
                    fontSize="small"
                    color='warning'

                    onClick={() => removeFilter(e, { filterCode: 'color', id: e })}
                  />

                  {
                    getSwatchImageValue(e)
                      ?
                      <img alt="cover image" className="cursor-pointer color-filter-item swatch-value-filter" src={'/uploads/' + getSwatchImageValue(e)} />

                      :
                      <Paragraph
                        className="cursor-pointer color-filter-item"
                        borderRadius={5}
                        border='solid 1px white'
                        backgroundColor={getSwatchValue(e) ? getSwatchValue(e) : getLabel(e)}
                      />
                  }

                </FlexBox>
              })}
            </FlexBox>
            <FlexBox mt={'5px'} flexWrap={'wrap'}>
              {context.filters.size?.map((e: any, ind: React.Key | null | undefined) => {
                return <FlexBox
                  alignItems={'center'}
                  borderRadius={10}
                  minWidth={'50px'}
                  pr={1}
                  pl={1}
                  border={'solid 1px white'}
                  ml={2}
                  key={ind}
                  justifyContent={'space-around'}

                >
                  <CloseSharpIcon
                    cursor="pointer"
                    fontSize="small"
                    onClick={() => removeFilter(ind, { filterCode: 'size', id: e })}
                  />
                  {getLabel(e)}

                </FlexBox>
              })}
            </FlexBox>

            <FlexBox mt={'5px'} flexWrap={'wrap'}>
              {context.filters.brand?.map((e: any, ind: React.Key | null | undefined) => {
                return <FlexBox
                  alignItems={'center'}
                  borderRadius={10}
                  minWidth={'50px'}
                  pr={1}
                  pl={1}
                  border={'solid 1px white'}
                  ml={2}
                  key={ind}
                  justifyContent={'space-around'}

                >
                  <CloseSharpIcon
                    cursor="pointer"
                    fontSize="small"
                    onClick={() => removeFilter(ind, { filterCode: 'brand', id: e })}
                  />
                  {getLabelBrand(e)}

                </FlexBox>
              })}
            </FlexBox>

          </FlexBox>
        }

        {/*---------------------------------------------- ΠΡΟΒΟΛΗ -----------------------------------------------*/}

        <FlexBox width={'100%'} alignItems='center' justifyContent={'space-between'}
          className='filter-item' >

          <Paragraph color="text.muted" ml="0.5rem">
            {t("filters.view")}
          </Paragraph>
          <Box>
            <IconButton size="small" backgroundColor={'transparent'} onClick={filters.toggleView("grid")}>
              <Icon
                variant="small"
                defaultcolor="auto"
                color={filters.view === "grid" ? "primary" : "grey"}
                className='view-button'
              >
                grid
              </Icon>
            </IconButton>
            {screenWidth > 768 &&
              <IconButton className="rotate" size="small" backgroundColor={'transparent'} onClick={filters.toggleView("grid3")}>
                <Icon
                  variant="small"
                  defaultcolor="auto"
                  color={filters.view === "grid3" ? "primary" : "grey"}
                  className='view-button'
                >
                  menu
                </Icon>
              </IconButton>}
            <IconButton backgroundColor={'transparent'} size="small" onClick={filters.toggleView("list")}>
              <Icon
                variant="small"
                defaultcolor="auto"
                color={filters.view === "list" ? "primary" : "grey"}
                className='view-button'
              >
                menu
              </Icon>
            </IconButton>
          </Box>
        </FlexBox>

        {/*-------------------------------------------- ΤΑΞΙΝΟΜΗΣΗ ANA ------------------------------------------*/}


        <AccordionWrapper className="filter-item">
          <Accordion key={sortOptions[0].label}>

            <AccordionHeader
              px="0px"
              py="6px"
              color="text.muted"
            // justifyContent="flex-start"
            >
              <Span className="cursor-pointer" ml="9px">
                {t("filters.sort")} {sortOption}
              </Span>
            </AccordionHeader>

            {sortOptions.map((option) => (
              <Paragraph
                className="cursor-pointer"
                fontSize="14px"
                color="text.muted"
                pl="22px"
                py="6px"
                key={option.label}
                onClick={() => handleSort(option)}
              >
                {option.label}
              </Paragraph>
            ))}
          </Accordion>
        </AccordionWrapper>

        {/*----------------------------- ΤΙΜΗ ------------------------------ */}


        {state.auth.isAuth &&
          <FlexBox flexDirection='column'>
            <SemiSpan className='price-header' > {t("filters.price")} </SemiSpan>
            <RangeSlider />
          </FlexBox>
        }


      </>
    )
  }

  function renderSelectElement(filter: { name: React.Key | null | undefined; options: any[]; code: string; }) {
    return (
      <>
        <AccordionWrapper className="filter-item">
          <Accordion key={filter.name} >
            <AccordionHeader
              px="0px"
              py="6px"
              color="text.muted"
            // justifyContent="flex-start"
            >
              <SemiSpan className="cursor-pointer" mr="9px">
                {filter.name}
              </SemiSpan>
            </AccordionHeader>
            <FlexBox className='color-filter-wrapper' >
              {filter.options.map((option: { label: React.Key | null | undefined; id: number; }) => (

                filter.code === 'color' ?



                  (option.swatch_color_image_value ?
                    <img key={option.id} onClick={() => handleFilter(option.swatch_color_image_value, filter.code, option.id)} alt="cover image" className="cursor-pointer color-filter-item swatch-value-filter" src={'/uploads/' + option.swatch_color_image_value} />
                    :
                    <Paragraph
                      className="cursor-pointer color-filter-item"
                      borderRadius={5}
                      border='solid 1px white'
                      py="6px"
                      key={option.label}
                      backgroundColor={option.swatch_value ? option.swatch_value : option.label}
                      onClick={() => handleFilter(option.label, filter.code, option.id)}
                      marginLeft={10}
                    //style={{ border: `solid 2px ${activeFilterIDs.includes(option.id) ? activeColor : "white"}` }}
                    >

                    </Paragraph>
                  )


                  :

                  <Paragraph
                    className="cursor-pointer sizes-filter-item"
                    color="text.muted"
                    textAlign='center'
                    key={option.label}
                    onClick={() => handleFilter(option.label, filter.code, option.id, filter.code)}
                    border={`solid 1px ${activeFilterIDs.includes(option.id) ? activeColor : "white"}`}
                  // style={{ border: `solid 1px ${activeFilterIDs.includes(option.id) ? activeColor : "white"}` }}
                  >
                    {option.label}
                  </Paragraph>
              ))}
            </FlexBox>
          </Accordion>
        </AccordionWrapper>
      </>
    );
  }

  return (

    <ProductFilterCardStyled>
      {!isSearch ? (
        filters?.filters?.map((filter: any, elementIndex: any) => (
          <FlexBox flexDirection="column" key={elementIndex} style={{ gap: '1rem' }}>
            {renderFilter(filter)}
          </FlexBox>
        ))
      ) :

        <>
          {state.auth.isAuth && categoryProducts?.length ?
            <FlexBox flexDirection="column">
              <SemiSpan className="price-header"> {t("filters.price")} </SemiSpan>
              <RangeSliderSearch />
            </FlexBox>
            : null}
        </>

      }
    </ProductFilterCardStyled>


  );
};




export default ProductFilterCard;
